// src/auth/AuthProvider.jsx
//
// Phase 2 Sprint 1: React Provider for Firebase Auth state.
// Phase 2 Sprint 2: Firestore profile bridge added (syncUserProfileToAppState helper).
//
// Per P5: imports AuthContext from AuthContext.jsx; exports AuthProvider.
// Per P6/V1: useAppState clearing uses setUser(null) per actual API (no clearUser exists).
// Per P7: getIdTokenClaims wrapped in useCallback; AdminGuard sees a stable reference.
// Per P2: behavior depends on VITE_AUTH_MODE tri-state.
// Per Sprint 2 v1.1-alpha: shared syncUserProfileToAppState called by signUp and onAuthStateChanged.
//   Intentional double-call accepted; ensureUserProfile is idempotent.
// Per Sprint 2 A5: onAuthStateChanged sync wrapped in try/catch/finally; setLoading(false) guaranteed.
// Per Sprint 2 v1.2-error-stance: onAuthStateChanged sync failure -> silent fallback (setUser(null) + console.error).

import React, { useState, useEffect, useCallback } from 'react';
import {
    onAuthStateChanged,
    getAdditionalUserInfo,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    updateProfile,
    signInWithPopup,
    GoogleAuthProvider,
    sendEmailVerification,
    sendPasswordResetEmail,
    signOut as firebaseSignOut,
} from 'firebase/auth';
import { trackAuthentication } from '../analytics/analytics.js';
import { AuthContext } from './AuthContext.jsx';
import { auth, AUTH_MODE } from './firebase.js';
import { ensureUserProfile, fetchUserProfile } from './profile.js';
import { setHistoryAccount } from '../storage/localChatHistory.js';

export const AuthProvider = ({ children, useAppState }) => {
    // NOTE: prop name "useAppState" carries spec fidelity; the value passed in is the
    // INSTANCE returned by the hook (state object with user + setUser), not the hook itself.
    // Firebase/emulator auth user (set by onAuthStateChanged). In mock mode the
    // effective currentUser is derived reactively from useAppState.user (below).
    const [firebaseUser, setFirebaseUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // Sprint 2 v1.1-alpha shared helper: ensure profile, fetch it, bridge into useAppState.
    // Called by signUp (awaited before resolve, eliminates header flash) and by
    // onAuthStateChanged (universal repair/bridge path). Double-call intentional.
    const syncUserProfileToAppState = async (user) => {
        await ensureUserProfile(user);
        const profile = await fetchUserProfile(user.uid);
        if (!profile) {
            throw new Error('syncUserProfileToAppState: profile null after ensure');
        }
        if (useAppState?.setUser) {
            useAppState.setUser({
                email: profile.email,
                name: profile.displayName,
                joined: profile.createdAt?.toDate?.()?.toISOString?.() ?? null,
                uid: profile.uid,
                isMock: false,
            });
        }
    };

    useEffect(() => {
        if (AUTH_MODE === 'mock') {
            // Mock mode: currentUser is derived from useAppState.user on every
            // render (see the computed `currentUser` below), so sign-in / sign-up
            // / sign-out within a session are reflected immediately. A mount-only
            // bridge here would miss in-session sign-in and leave AuthGuard
            // bouncing authenticated users to /login. Nothing to subscribe to.
            setLoading(false);
            return;
        }
        if (!auth) {
            console.error('AuthProvider: auth instance missing in mode', AUTH_MODE);
            setLoading(false);
            return;
        }
        // Sprint 2 A5: try/catch/finally guarantees setLoading(false) runs in all branches.
        // v1.2-error-stance: on sync failure, log + setUser(null); user sees signed-out UI
        // despite valid Auth session. Recovery: page reload re-fires onAuthStateChanged.
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            await setHistoryAccount(user?.uid || null);
            setFirebaseUser(user);
            try {
                if (user) {
                    await syncUserProfileToAppState(user);
                } else if (useAppState?.setUser) {
                    useAppState.setUser(null);
                }
            } catch (err) {
                console.error('AuthProvider: profile sync failed during auth state change:', err);
                if (useAppState?.setUser) {
                    useAppState.setUser(null);
                }
            } finally {
                setLoading(false);
            }
        });
        return unsubscribe;
    }, []);

    const signIn = async (email, password) => {
        if (AUTH_MODE === 'mock') {
            if (useAppState?.setUser) {
                useAppState.setUser({ email, name: email.split('@')[0], joined: new Date().toISOString(), isMock: true });
            } else {
                throw new Error('AuthProvider mock mode: useAppState.setUser unavailable');
            }
            return { user: { email, isMock: true } };
        }
        if (!auth) throw new Error(`signIn unavailable in mode "${AUTH_MODE}"`);
        // Sprint 3 v1.3: mirror signUp's awaited-sync pattern. Sprint 3 section C-X9 found
        // that onAuthStateChanged-only repair is insufficient in the doc-delete recovery
        // path (signin completes, dashboard renders signed-out-equivalent UI, manual
        // refresh required). Explicit awaited sync removes this dependency for the signin
        // path. onAuthStateChanged remains for reload/cold-start/existing-session repair.
        const credential = await signInWithEmailAndPassword(auth, email, password);
        await syncUserProfileToAppState(credential.user);
        trackAuthentication(credential.user, 'login', 'email');
        return credential;
    };

    const signUp = async (email, password, name) => {
        if (AUTH_MODE === 'mock') {
            if (useAppState?.setUser) {
                useAppState.setUser({
                    email,
                    name: name || email.split('@')[0],
                    joined: new Date().toISOString(),
                    isMock: true,
                });
            } else {
                throw new Error('AuthProvider mock mode: useAppState.setUser unavailable');
            }
            return { user: { email, isMock: true } };
        }
        if (!auth) throw new Error(`signUp unavailable in mode "${AUTH_MODE}"`);
        const credential = await createUserWithEmailAndPassword(auth, email, password);
        if (name && credential.user) {
            await updateProfile(credential.user, { displayName: name });
        }
        // Fire-and-forget verification email (gates free AI use; see AiStudyPartnerPanel
        // + the onAiChat backend gate). Never blocks sign-up; failures are logged only.
        // Google sign-in is already email_verified=true, so it skips this path entirely.
        if (credential.user) {
            sendEmailVerification(credential.user).catch((e) => console.error('sendEmailVerification failed:', e));
        }
        // Sprint 2 v1.1-alpha: await sync before returning so LoginPage's navigation
        // happens with useAppState.user already populated (no header flash).
        // Errors propagate to LoginPage's existing try/catch around signUp.
        await syncUserProfileToAppState(credential.user);
        trackAuthentication(credential.user, 'sign_up', 'email');
        return credential;
    };

    const signInWithGoogle = async () => {
        if (AUTH_MODE === 'mock') {
            throw new Error('Google Sign-In requires emulator or firebase mode (not available in mock mode)');
        }
        if (!auth) throw new Error(`signInWithGoogle unavailable in mode "${AUTH_MODE}"`);
        const provider = new GoogleAuthProvider();
        // Sprint 3 v1.3: mirror signUp/signIn's awaited-sync pattern (see signIn for
        // context). Removes onAuthStateChanged dependency for explicit Google signin path.
        const credential = await signInWithPopup(auth, provider);
        await syncUserProfileToAppState(credential.user);
        trackAuthentication(credential.user, getAdditionalUserInfo(credential)?.isNewUser ? 'sign_up' : 'login', 'google');
        return credential;
    };

    const signOutUser = async () => {
        await setHistoryAccount(null);
        if (AUTH_MODE !== 'mock' && auth) {
            await firebaseSignOut(auth);
        }
        // V1 adapter: spec section 2.3 used clearUser(); actual useAppState API exposes setUser only.
        // Phase 1 pattern at AccountPage.jsx:448 already uses setUser(null) for sign-out.
        if (useAppState?.setUser) {
            useAppState.setUser(null);
        }
        setFirebaseUser(null);
    };

    // Password reset (additive; does NOT sign anyone in, so the Sprint-3 v1.3
    // awaited sync barrier is untouched). Sends Firebase's reset email; the
    // caller (LoginPage) owns the success/error/privacy UX. Google accounts have
    // no password -- Firebase simply won't send for them, handled in the UI.
    const sendPasswordReset = async (email) => {
        if (AUTH_MODE === 'mock') {
            // Mock mode has no Firebase backend: simulate a successful send
            // (no-op resolve), mirroring the other auth fns' mock behavior.
            return;
        }
        if (!auth) throw new Error(`sendPasswordReset unavailable in mode "${AUTH_MODE}"`);
        await sendPasswordResetEmail(auth, email);
    };

    const getIdTokenClaims = useCallback(async () => {
        if (AUTH_MODE === 'mock') return null;
        if (!auth?.currentUser) return null;
        try {
            const tokenResult = await auth.currentUser.getIdTokenResult();
            return tokenResult.claims;
        } catch (err) {
            console.error('getIdTokenClaims failed:', err);
            return null;
        }
    }, []);

    // In mock mode the source of truth for "who is signed in" is useAppState.user,
    // so derive currentUser from it reactively -- guards and listeners then react
    // to in-session mock sign-in/out (fixes authenticated users bouncing to
    // /login). In firebase/emulator modes, currentUser is the Firebase user.
    const mockUser = useAppState?.user;
    const currentUser = AUTH_MODE === 'mock'
        ? (mockUser
            ? { email: mockUser.email, displayName: mockUser.name, uid: mockUser.uid, isMock: true }
            : null)
        : firebaseUser;

    const value = {
        currentUser,
        loading,
        signIn,
        signUp,
        signInWithGoogle,
        signOut: signOutUser,
        sendPasswordReset,
        getIdTokenClaims,
        authMode: AUTH_MODE,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
